<script setup lang="ts">
import type { FieldDefinition } from '@/types/calculator'

defineProps<{
  id: string
  modelValue: string
  label?: string
  options: NonNullable<FieldDefinition['options']>
  required?: boolean
  error?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()
</script>

<template>
  <fieldset class="field">
    <legend v-if="label" class="field__label">{{ label }}</legend>
    <div class="field__options">
      <label
        v-for="opt in options"
        :key="opt.value"
        class="field__option"
        :class="{ 'field__option--selected': modelValue === opt.value }"
      >
        <input
          :name="id"
          type="radio"
          :value="opt.value"
          :checked="modelValue === opt.value"
          :required="required"
          @change="emit('update:modelValue', opt.value)"
        />
        <span>{{ opt.label }}</span>
      </label>
    </div>
    <p v-if="error" class="field__error" role="alert">{{ error }}</p>
  </fieldset>
</template>

<style scoped>
.field {
  border: 0;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.field__label {
  font-weight: var(--font-medium);
  font-size: var(--text-sm);
}

.field__options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.field__option {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
}

.field__option:hover {
  border-color: var(--color-primary-light);
}

.field__option--selected {
  border-color: var(--color-primary);
  background-color: rgb(10 92 86 / 0.08);
}

.field__option input {
  accent-color: var(--color-primary);
}

.field__error {
  font-size: var(--text-sm);
  color: var(--color-error);
}
</style>
